import axios from 'axios';
import { getAuthHeader } from './authService';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

export interface ConsentType {
  id: number;
  name: string;
  code: string;
  description: string;
  content: string;
  category?: string;
  isMandatory: boolean;
  requiresSignature: boolean;
  expiryDays?: number;
  universityId: number;
  createdByUserId: number;
  createdByUserType: string;
  createdAt: string;
  updatedAt: string;
}

export interface ConsentResponse {
  id: number;
  userId: number;
  userType: string;
  consentTypeId: number;
  consentType: ConsentType;
  status: 'GIVEN' | 'WITHDRAWN' | 'EXPIRED';
  givenAt: string;
  withdrawnAt?: string;
  expiresAt?: string;
  signatureData?: string;
  ipAddress?: string;
  userAgent?: string;
  withdrawalReason?: string;
}

export interface ConsentRequest {
  consentTypeId: number;
  signatureData?: string;
}

export interface ComplianceStatus {
  isCompliant: boolean;
  mandatoryConsentCount: number;
  userId: number;
  userType: string;
  mandatoryConsents?: ConsentType[];
}

class ConsentService {
  // Get all consent types
  async getConsentTypes(): Promise<ConsentType[]> {
    try {
      const response = await axios.get(`${API_BASE_URL}/consent/types`, {
        headers: getAuthHeader()
      });
      return response.data;
    } catch (error: any) {
      console.error('Error fetching consent types:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch consent types');
    }
  }

  // Get mandatory consent types for current user
  async getMandatoryConsentTypes(): Promise<ConsentType[]> {
    try {
      const response = await axios.get(`${API_BASE_URL}/consent/types/mandatory`, {
        headers: getAuthHeader()
      });
      return response.data;
    } catch (error: any) {
      console.error('Error fetching mandatory consent types:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch mandatory consent types');
    }
  }

  // Get specific consent type
  async getConsentType(id: number): Promise<ConsentType> {
    try {
      const response = await axios.get(`${API_BASE_URL}/consent/types/${id}`, {
        headers: getAuthHeader()
      });
      return response.data;
    } catch (error: any) {
      console.error('Error fetching consent type:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch consent type');
    }
  }

  // Get user's consents
  async getMyConsents(): Promise<ConsentResponse[]> {
    try {
      const response = await axios.get(`${API_BASE_URL}/consent/my-consents`, {
        headers: getAuthHeader()
      });
      return response.data;
    } catch (error: any) {
      console.error('Error fetching user consents:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch consents');
    }
  }

  // Get specific consent by type
  async getMyConsentByType(consentTypeId: number): Promise<ConsentResponse> {
    try {
      const response = await axios.get(`${API_BASE_URL}/consent/my-consents/${consentTypeId}`, {
        headers: getAuthHeader()
      });
      return response.data;
    } catch (error: any) {
      console.error('Error fetching consent by type:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch consent');
    }
  }

  // Give consent
  async giveConsent(request: ConsentRequest): Promise<ConsentResponse> {
    try {
      const response = await axios.post(`${API_BASE_URL}/consent/give`, request, {
        headers: getAuthHeader()
      });
      return response.data;
    } catch (error: any) {
      console.error('Error giving consent:', error);
      throw new Error(error.response?.data?.message || 'Failed to give consent');
    }
  }

  // Withdraw consent
  async withdrawConsent(consentTypeId: number, reason?: string): Promise<ConsentResponse> {
    try {
      const params = reason ? `?reason=${encodeURIComponent(reason)}` : '';
      const response = await axios.post(`${API_BASE_URL}/consent/withdraw/${consentTypeId}${params}`, {}, {
        headers: getAuthHeader()
      });
      return response.data;
    } catch (error: any) {
      console.error('Error withdrawing consent:', error);
      throw new Error(error.response?.data?.message || 'Failed to withdraw consent');
    }
  }

  // Renew consent
  async renewConsent(consentTypeId: number, signatureData?: string): Promise<ConsentResponse> {
    try {
      const params = signatureData ? `?signatureData=${encodeURIComponent(signatureData)}` : '';
      const response = await axios.post(`${API_BASE_URL}/consent/renew/${consentTypeId}${params}`, {}, {
        headers: getAuthHeader()
      });
      return response.data;
    } catch (error: any) {
      console.error('Error renewing consent:', error);
      throw new Error(error.response?.data?.message || 'Failed to renew consent');
    }
  }

  // Check consent validity
  async checkConsent(consentCode: string): Promise<{
    consentCode: string;
    hasValidConsent: boolean;
    userId: number;
    userType: string;
  }> {
    try {
      const response = await axios.get(`${API_BASE_URL}/consent/check/${consentCode}`, {
        headers: getAuthHeader()
      });
      return response.data;
    } catch (error: any) {
      console.error('Error checking consent:', error);
      throw new Error(error.response?.data?.message || 'Failed to check consent');
    }
  }

  // Check compliance status
  async checkComplianceStatus(): Promise<ComplianceStatus> {
    try {
      const response = await axios.get(`${API_BASE_URL}/consent/compliance-status`, {
        headers: getAuthHeader()
      });
      return response.data;
    } catch (error: any) {
      console.error('Error checking compliance status:', error);
      throw new Error(error.response?.data?.message || 'Failed to check compliance status');
    }
  }

  // Admin endpoints
  async createConsentType(data: {
    name: string;
    code: string;
    description: string;
    content: string;
    category?: string;
    isMandatory: boolean;
    requiresSignature: boolean;
    expiryDays?: number;
  }): Promise<ConsentType> {
    try {
      const params = new URLSearchParams({
        name: data.name,
        code: data.code,
        description: data.description,
        content: data.content,
        isMandatory: String(data.isMandatory),
        requiresSignature: String(data.requiresSignature),
      });
      
      if (data.category) params.append('category', data.category);
      if (data.expiryDays) params.append('expiryDays', String(data.expiryDays));
      
      const response = await axios.post(`${API_BASE_URL}/consent/types?${params.toString()}`, null, {
        headers: getAuthHeader()
      });
      return response.data;
    } catch (error: any) {
      console.error('Error creating consent type:', error);
      throw new Error(error.response?.data?.message || 'Failed to create consent type');
    }
  }

  async getExpiringConsents(daysBeforeExpiry: number = 30): Promise<ConsentResponse[]> {
    try {
      const response = await axios.get(`${API_BASE_URL}/consent/expiring`, {
        headers: getAuthHeader(),
        params: { daysBeforeExpiry }
      });
      return response.data;
    } catch (error: any) {
      console.error('Error fetching expiring consents:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch expiring consents');
    }
  }

  // Helper methods
  isConsentExpired(consent: ConsentResponse): boolean {
    if (!consent.expiresAt) return false;
    return new Date(consent.expiresAt) < new Date();
  }

  formatConsentDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString();
  }

  getConsentStatusColor(status: string): string {
    switch (status) {
      case 'GIVEN': return 'success';
      case 'WITHDRAWN': return 'error';
      case 'EXPIRED': return 'warning';
      default: return 'default';
    }
  }

  // Template management methods
  async getTemplates(): Promise<any[]> {
    try {
      const response = await axios.get(`${API_BASE_URL}/consent/templates`, {
        headers: getAuthHeader()
      });
      return response.data;
    } catch (error: any) {
      console.error('Error fetching consent templates:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch consent templates');
    }
  }

  async getTemplate(id: number): Promise<any> {
    try {
      const response = await axios.get(`${API_BASE_URL}/consent/templates/${id}`, {
        headers: getAuthHeader()
      });
      return response.data;
    } catch (error: any) {
      console.error('Error fetching consent template:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch consent template');
    }
  }

  async createTemplate(template: any): Promise<any> {
    try {
      const response = await axios.post(`${API_BASE_URL}/consent/templates`, template, {
        headers: getAuthHeader()
      });
      return response.data;
    } catch (error: any) {
      console.error('Error creating consent template:', error);
      throw new Error(error.response?.data?.message || 'Failed to create consent template');
    }
  }

  async updateTemplate(id: number, template: any): Promise<any> {
    try {
      const response = await axios.put(`${API_BASE_URL}/consent/templates/${id}`, template, {
        headers: getAuthHeader()
      });
      return response.data;
    } catch (error: any) {
      console.error('Error updating consent template:', error);
      throw new Error(error.response?.data?.message || 'Failed to update consent template');
    }
  }

  async deleteTemplate(id: number): Promise<void> {
    try {
      await axios.delete(`${API_BASE_URL}/consent/templates/${id}`, {
        headers: getAuthHeader()
      });
    } catch (error: any) {
      console.error('Error deleting consent template:', error);
      throw new Error(error.response?.data?.message || 'Failed to delete consent template');
    }
  }
}

export const consentService = new ConsentService();